/**
 * Refreshing the rate card, for real.
 *
 * The library ships a dated price snapshot, which is right for reproducibility
 * and wrong for a buyer: rate cards move, and an estimate quoted on a three-month
 * old grid is quietly wrong. OpenRouter publishes the whole grid at a public,
 * CORS-open endpoint with no key, so the interface can check it on demand rather
 * than pretend to.
 *
 * Two rules, both there so a refresh can never make the answer worse:
 *
 *  1. Only models the library already prices are read back. The endpoint lists
 *     440+ models; adopting them would put rows in the table that no measurement,
 *     no tier and no capability score stands behind.
 *  2. A model the endpoint no longer lists keeps its snapshot price and is
 *     reported as stale. Dropping the row would silently change the ranking.
 *
 * The field mapping mirrors tools/extract_usage.py exactly, including the one
 * that matters: `cache_write` stays null when the provider publishes none, so
 * `cacheWritePrice()` can apply its floor instead of billing a write as free.
 */
import type { ModelPrice } from './types'

export const PRICING_ENDPOINT = 'https://openrouter.ai/api/v1/models'

export interface PricingRefresh {
  models: ModelPrice[]
  /** ISO instant the grid was read. */
  fetchedAt: string
  /** Models whose price came back from the endpoint. */
  matched: number
  /** Models the endpoint no longer lists: snapshot price kept, and said so. */
  stale: string[]
  /** Models whose published price was not usable: snapshot kept, and said so. */
  rejected: string[]
  /** Ids whose price actually moved, with the direction. */
  changed: { id: string; label: string; ratio: number }[]
}

interface UpstreamModel {
  id?: unknown
  context_length?: unknown
  pricing?: Record<string, unknown>
}

function num(raw: unknown, fallback: number | null): number | null {
  if (raw === null || raw === undefined || raw === '') return fallback
  const value = Number(raw)
  return Number.isFinite(value) ? value : fallback
}

/**
 * A published price of zero is a free variant, not a cheaper model.
 *
 * `num()` only rejects the non-finite and the empty string, so `num("0", …)`
 * returns 0 — and OpenRouter does publish free variants. One adopted zero puts a
 * model at the top of the cost ranking, into the `cheapest` pick and possibly
 * into `value`: the product would have invented its most visible recommendation.
 */
function positive(raw: unknown): number | null {
  const value = num(raw, null)
  return value !== null && value > 0 ? value : null
}

/** Total per-token cost of one model, used only to tell whether a price moved. */
function priceSignature(model: ModelPrice): number {
  return model.prompt + model.completion + model.cache_read + (model.cache_write ?? 0)
}

export async function fetchPricing(
  known: ModelPrice[],
  options: { signal?: AbortSignal; endpoint?: string } = {},
): Promise<PricingRefresh | null> {
  let payload: { data?: unknown }
  try {
    const response = await fetch(options.endpoint ?? PRICING_ENDPOINT,
      options.signal ? { signal: options.signal } : {})
    if (!response.ok) return null
    payload = (await response.json()) as { data?: unknown }
  } catch {
    return null // offline, blocked, or the endpoint moved: keep the snapshot
  }

  const upstream = new Map<string, UpstreamModel>()
  for (const entry of Array.isArray(payload.data) ? payload.data : []) {
    const model = entry as UpstreamModel
    if (typeof model.id === 'string') upstream.set(model.id, model)
  }
  if (upstream.size === 0) return null

  const models: ModelPrice[] = []
  const stale: string[] = []
  const rejected: string[] = []
  const changed: PricingRefresh['changed'] = []

  for (const model of known) {
    const found = upstream.get(model.id)
    const pricing = found?.pricing
    if (!found || !pricing) {
      stale.push(model.label)
      models.push(model)
      continue
    }
    // A model whose headline input price is not a positive number is not
    // refreshed AT ALL: a partial adoption would mix today's output price with a
    // snapshot input price and produce a figure from no single rate card.
    const prompt = positive(pricing['prompt'])
    if (prompt === null) {
      rejected.push(model.label)
      models.push(model)
      continue
    }
    const next: ModelPrice = {
      ...model,
      prompt,
      completion: positive(pricing['completion']) ?? model.completion,
      cache_read: positive(pricing['input_cache_read']) ?? model.cache_read,
      // null is meaningful and must survive: it means the provider publishes no
      // cache-write price, not that writing to cache is free.
      cache_write: positive(pricing['input_cache_write']),
      context: num(found.context_length, model.context),
    }
    const before = priceSignature(model)
    const after = priceSignature(next)
    if (before > 0 && Math.abs(after - before) / before > 1e-9) {
      changed.push({ id: model.id, label: model.label, ratio: after / before })
    }
    models.push(next)
  }

  return {
    models,
    fetchedAt: new Date().toISOString(),
    matched: models.length - stale.length - rejected.length,
    stale,
    rejected,
    changed,
  }
}
